/**
 * Custom order builder.
 *
 * Drives the running summary panel, rolls the color checkboxes up into a single
 * submitted field, keeps validation friendly enough for a booth, and resets the
 * kiosk between visitors.
 *
 * Deliberately standalone (no @theme/component): this runs on a marketing page
 * and on a tablet at an event, and should not break if the theme's internal
 * component API moves.
 */

/** Order the summary rows read in, regardless of which steps are present. */
const SUMMARY_ORDER = ['Product', 'Theme', 'Details', 'Colors', 'Quantity', 'Timing', 'Name'];

class CustomOrderBuilder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');

    // The confirmation state renders no form; only the reset timer applies.
    if (!this.form) {
      this.#startResetTimer();
      return;
    }

    this.grid = this.querySelector('[data-co-grid]');
    this.done = this.querySelector('[data-co-done]');
    this.summary = this.querySelector('[data-co-summary]');
    this.summaryEmpty = this.querySelector('[data-co-summary-empty]');
    this.error = this.querySelector('[data-co-error]');

    // Take validation over from the browser: a card group's real input is
    // visually hidden, so the native bubble would point at nothing. Set in JS
    // rather than markup so native validation still applies if this never runs.
    this.form.noValidate = true;

    this.form.addEventListener('change', () => this.#sync());
    this.form.addEventListener('input', () => this.#sync());
    this.form.addEventListener('submit', (event) => this.#onSubmit(event));
    this.submitButton = this.querySelector('.co-builder__submit');

    this.#sync();
  }

  /** Collect every answer, in a stable order, as [label, value] pairs. */
  #answers() {
    const values = new Map();

    for (const input of this.form.querySelectorAll('[data-co-input]')) {
      const key = input.dataset.coInput;

      if (input.type === 'checkbox' || input.type === 'radio') {
        if (!input.checked) continue;
        // Checkboxes accumulate; radios replace.
        if (input.type === 'checkbox') {
          values.set(key, [...(values.get(key) ?? []), input.value]);
        } else {
          values.set(key, [input.value]);
        }
        continue;
      }

      const text = input.value.trim();
      if (text) values.set(key, [text]);
    }

    return values;
  }

  /** Mirror the answers into the summary panel and the hidden collect fields. */
  #sync() {
    const values = this.#answers();

    // Multi-select groups post as one comma-joined field.
    for (const hidden of this.form.querySelectorAll('[data-co-collect]')) {
      hidden.value = (values.get(hidden.dataset.coCollect) ?? []).join(', ');
    }

    if (!this.summary) return;

    const keys = [...values.keys()].sort((a, b) => {
      const ai = SUMMARY_ORDER.indexOf(a);
      const bi = SUMMARY_ORDER.indexOf(b);
      return (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) - (bi === -1 ? Number.MAX_SAFE_INTEGER : bi);
    });

    this.summary.replaceChildren(
      ...keys.flatMap((key) => {
        const dt = document.createElement('dt');
        dt.textContent = key;
        const dd = document.createElement('dd');
        dd.textContent = values.get(key).join(', ');
        return [dt, dd];
      })
    );

    if (this.summaryEmpty) this.summaryEmpty.hidden = keys.length > 0;
  }

  /**
   * Check the same constraints the browser would, then point at the first thing
   * that needs attention rather than leaving the shopper hunting for it.
   */
  async #onSubmit(event) {
    if (this.form.checkValidity()) {
      this.#hideError();

      // Post in the background and confirm in place — Shopify's contact
      // redirect does not reliably come back to this page.
      if (this.done && window.fetch) {
        event.preventDefault();
        await this.#send();
      }
      return;
    }

    event.preventDefault();

    const invalid = this.form.querySelector(':invalid');
    if (!invalid) return;

    // Say what is wrong before moving the page: if scrolling or focusing fails,
    // the shopper is still told why the form did not send.
    const isChoice = invalid.type === 'radio' || invalid.type === 'checkbox';
    this.#showError(
      isChoice ? 'Please finish the highlighted step.' : 'Please check the highlighted field.'
    );

    if (!isChoice) invalid.focus({ preventScroll: true });

    // A card group's real input is visually hidden, so scroll to its step.
    const target = invalid.closest('.co-step') ?? invalid;
    target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Post to Shopify's contact form without leaving the page.
   *
   * Submitting normally would follow Shopify's redirect, which does not
   * reliably return here, so the shopper would land somewhere they did not ask
   * to go and never see the confirmation.
   *
   * Only a thrown fetch means nothing reached Shopify, and only then is it safe
   * to offer a retry — a resolved response is treated as submitted so a shopper
   * can never send the same request twice.
   */
  async #send() {
    this.#setBusy(true);

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Contact form responded ${response.status}`);

      this.#showConfirmation();
    } catch (error) {
      console.error('Custom order submit failed', error);
      this.#setBusy(false);
      this.#showError('That did not send — please check your connection and try again.');
    }
  }

  #setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', String(busy));
  }

  /** Swap the form out for the confirmation and hand focus to it. */
  #showConfirmation() {
    if (this.grid) this.grid.hidden = true;
    this.done.hidden = false;
    this.done.setAttribute('tabindex', '-1');
    this.done.focus({ preventScroll: true });
    this.done.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    this.#startResetTimer();
  }

  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  #hideError() {
    if (!this.error) return;
    this.error.hidden = true;
  }

  /** Kiosk only: hand the tablet back to the next visitor on its own. */
  #startResetTimer() {
    const seconds = Number(this.dataset.autoReset ?? 0);
    const url = this.dataset.resetUrl;
    if (!seconds || !url) return;

    const readout = this.querySelector('[data-co-timer-value]');
    let left = seconds;

    const tick = () => {
      left -= 1;
      if (readout) readout.textContent = String(Math.max(left, 0));
      if (left <= 0) {
        clearInterval(timer);
        window.location.assign(url);
      }
    };

    const timer = setInterval(tick, 1000);

    // Any sign of life means someone is still reading it.
    const cancel = () => {
      clearInterval(timer);
      const note = this.querySelector('[data-co-timer]');
      if (note) note.hidden = true;
    };

    for (const evt of ['pointerdown', 'keydown', 'wheel']) {
      this.addEventListener(evt, cancel, { once: true, passive: true });
    }
  }
}

if (!customElements.get('custom-order-builder')) {
  customElements.define('custom-order-builder', CustomOrderBuilder);
}
